import { z } from "zod";
// ============================================
// Zod Schemas for oRPC Contracts
// ============================================
export const OverlappingShiftWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftIds: z.array(z.uuid()),
    overlapStart: z.iso.datetime(),
    overlapEnd: z.iso.datetime(),
    overlapHours: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const InsufficientLaborWarningSchema = z.object({
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
    date: z.string(),
    forecastedHours: z.number(),
    scheduledHours: z.number(),
    gap: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const InsufficientSkilledLaborWarningSchema = z.object({
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
    requiredSkills: z.array(z.string()),
    period: z.iso.datetime(),
    forecastedHours: z.number(),
    scheduledHours: z.number(),
    gap: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const OverstaffedPeriodWarningSchema = z.object({
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
    date: z.string(),
    forecastedHours: z.number(),
    scheduledHours: z.number(),
    overage: z.number(),
    overagePercentage: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const DailyOvertimeWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    date: z.string(),
    hours: z.number(),
    threshold: z.number(),
    overtime: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const WeeklyOvertimeWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    weekStart: z.string(),
    hours: z.number(),
    threshold: z.number(),
    overtime: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const ExpiredCertificationWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftId: z.uuid(),
    skillId: z.uuid(),
    skillName: z.string(),
    expiryDate: z.string(),
    daysUntilExpiry: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const UntrainedEmployeeWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftId: z.uuid(),
    requiredSkillId: z.uuid(),
    requiredSkillName: z.string(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const ClopeningWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    closingShiftId: z.uuid(),
    openingShiftId: z.uuid(),
    closingEnd: z.iso.datetime(),
    openingStart: z.iso.datetime(),
    restGap: z.number(),
    minRestRequired: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const MaxConsecutiveWorkdaysWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    consecutiveDays: z.number(),
    maxDaysAllowed: z.number(),
    startDate: z.string(),
    endDate: z.string(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const InsufficientNoticeWarningSchema = z.object({
    firstShiftDate: z.string(),
    publishDate: z.string(),
    daysNotice: z.number(),
    daysRequired: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const UnavailableEmployeeWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftId: z.uuid(),
    shiftStart: z.iso.datetime(),
    shiftEnd: z.iso.datetime(),
    unavailabilityReason: z.string().optional(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const ConsecutiveClosingOpeningWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftIds: z.array(z.uuid()),
    type: z.enum(["CLOSING", "OPENING"]),
    count: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const UnevenDistributionWarningSchema = z.object({
    category: z.string(),
    weekStart: z.string().optional(),
    avgHours: z.number(),
    threshold: z.number(),
    employees: z.array(z.object({
        employeeId: z.uuid(),
        employeeName: z.string(),
        hours: z.number(),
        variance: z.number(),
        variancePercentage: z.number(),
    })),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const MaxWeekendHoursWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    weekStart: z.string(),
    weekendHours: z.number(),
    maxWeekendHours: z.number(),
    overage: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const SplitShiftPremiumWarningSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    shiftIds: z.array(z.uuid()),
    date: z.string(),
    gap: z.number(),
    maxGapAllowed: z.number(),
    fingerprint: z.string().optional(),
    overrideId: z.string().nullable().optional(),
});
export const ScheduleWarningsResponseSchema = z.object({
    staffing: z.object({
        overlappingShifts: z.array(OverlappingShiftWarningSchema),
        insufficientLabor: z.array(InsufficientLaborWarningSchema),
        insufficientSkilledLabor: z.array(InsufficientSkilledLaborWarningSchema),
        overstaffing: z.array(OverstaffedPeriodWarningSchema),
    }),
    compliance: z.object({
        dailyOvertime: z.array(DailyOvertimeWarningSchema),
        weeklyOvertime: z.array(WeeklyOvertimeWarningSchema),
        expiredCertifications: z.array(ExpiredCertificationWarningSchema),
        untrainedEmployees: z.array(UntrainedEmployeeWarningSchema),
    }),
    scheduling: z.object({
        clopenings: z.array(ClopeningWarningSchema),
        maxConsecutiveWorkdays: z.array(MaxConsecutiveWorkdaysWarningSchema),
        insufficientNotice: z.array(InsufficientNoticeWarningSchema),
        unavailableEmployees: z.array(UnavailableEmployeeWarningSchema),
        consecutiveClosingOpening: z.array(ConsecutiveClosingOpeningWarningSchema),
    }),
    distribution: z.object({
        unevenDistribution: z.array(UnevenDistributionWarningSchema),
        maxWeekendHours: z.array(MaxWeekendHoursWarningSchema),
        splitShiftPremium: z.array(SplitShiftPremiumWarningSchema),
    }),
});
